import React, { useEffect, useState } from "react";
import {
  DateField,
  FunctionField,
  List,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  DeleteOutline as DeleteIcon,
  PauseCircleOutline as PauseIcon,
  PlayCircleOutline as ResumeIcon,
  ScheduleOutlined as RescheduleIcon,
} from "@mui/icons-material";
import { CustomDatagrid } from "@/components/common";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  getAccountsApplications,
  getAccountsGroups,
  getAllAccounts,
} from "@/features/test/services/testService";
import type { AccountListItem } from "@/features/test/types/notificationTest";
import type { ScheduledJobRecord } from "../types/scheduledJob";
import NotificationDetailsDialog from "./NotificationDetailsDialog";
import {
  deleteSchedule,
  setScheduleStatus,
  reschedule,
} from "../services/scheduledJobService";
import { useCanEdit } from "@/utils/permissions";

const i18nBase = "resources.scheduled-jobs";

interface RowActionContext {
  openDetails: (record: ScheduledJobRecord) => void;
  onDelete: (record: ScheduledJobRecord) => void;
  onToggle: (record: ScheduledJobRecord) => void;
  onReschedule: (record: ScheduledJobRecord) => void;
  canEdit: boolean;
  busyId: string | null;
}

const RowActionContext = React.createContext<RowActionContext | null>(null);

const useRowActions = (): RowActionContext => {
  const ctx = React.useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action used outside ScheduledJobsList");
  }
  return ctx;
};

const ActionsCell: React.FC = () => {
  const record = useRecordContext<ScheduledJobRecord>();
  const translate = useTranslate();
  const { onDelete, onToggle, onReschedule, canEdit, busyId } = useRowActions();
  if (!record || !canEdit) return null;
  const isRecurring = /RECUR|PAUS/i.test(record.status ?? "");
  const isPaused = /PAUS/i.test(record.status ?? "");
  const busy = busyId === record.id;
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip title={translate(`${i18nBase}.actions.reschedule`)}>
        <span>
          <IconButton size="small" disabled={busy} onClick={(e) => { e.stopPropagation(); onReschedule(record); }}>
            <RescheduleIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {isRecurring && (
        <Tooltip title={translate(`${i18nBase}.actions.${isPaused ? "resume" : "pause"}`)}>
          <span>
            <IconButton size="small" disabled={busy} onClick={(e) => { e.stopPropagation(); onToggle(record); }}>
              {isPaused ? <ResumeIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title={translate(`${i18nBase}.actions.delete`)}>
        <span>
          <IconButton size="small" color="error" disabled={busy} onClick={(e) => { e.stopPropagation(); onDelete(record); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const NotificationCell: React.FC = () => {
  const record = useRecordContext<ScheduledJobRecord>();
  const translate = useTranslate();
  const { openDetails } = useRowActions();
  if (!record) return null;
  const notification = record.notification;
  if (!notification) return <span />;
  const preview = JSON.stringify(notification).slice(0, 20);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box
        component="span"
        sx={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 12,
          color: "text.secondary",
        }}
      >
        {preview}...
      </Box>
      <Tooltip title={translate(`${i18nBase}.actions.viewDetails`)}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openDetails(record);
          }}
          aria-label="view notification details"
          sx={{ color: "primary.main" }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

interface FilterBarProps {
  accountId: string;
  applicationId: string;
  groupId: string;
  accounts: AccountListItem[];
  applications: string[];
  groups: string[];
  onAccountChange: (id: string) => void;
  onApplicationChange: (id: string) => void;
  onGroupChange: (id: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  accountId,
  applicationId,
  groupId,
  accounts,
  applications,
  groups,
  onAccountChange,
  onApplicationChange,
  onGroupChange,
}) => {
  const translate = useTranslate();
  return (
    <Card sx={{ mb: 2, border: 1, borderColor: "divider" }}>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <MuiTextField
              select
              fullWidth
              label={translate(`${i18nBase}.filters.account`)}
              InputLabelProps={{ shrink: true }}
              value={accountId}
              onChange={(e) => onAccountChange(e.target.value)}
            >
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.accountName}
                </MenuItem>
              ))}
            </MuiTextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MuiTextField
              select
              fullWidth
              disabled={!accountId}
              label={translate(`${i18nBase}.filters.application`)}
              InputLabelProps={{ shrink: true }}
              value={applicationId}
              onChange={(e) => onApplicationChange(e.target.value)}
            >
              <MenuItem value="">
                <em>{translate(`${i18nBase}.filters.all`)}</em>
              </MenuItem>
              {applications.map((app) => (
                <MenuItem key={app} value={app}>
                  {app}
                </MenuItem>
              ))}
            </MuiTextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MuiTextField
              select
              fullWidth
              disabled={!applicationId}
              label={translate(`${i18nBase}.filters.group`)}
              InputLabelProps={{ shrink: true }}
              value={groupId}
              onChange={(e) => onGroupChange(e.target.value)}
            >
              <MenuItem value="">
                <em>{translate(`${i18nBase}.filters.all`)}</em>
              </MenuItem>
              {groups.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </MuiTextField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const ScheduledJobsList: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const [accounts, setAccounts] = useState<AccountListItem[]>([]);
  const [applications, setApplications] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [accountId, setAccountId] = useState<string>("");
  const [applicationId, setApplicationId] = useState<string>("");
  const [groupId, setGroupId] = useState<string>("");
  const [details, setDetails] = useState<Record<string, unknown> | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScheduledJobRecord | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<ScheduledJobRecord | null>(null);
  const [reDatetime, setReDatetime] = useState<string>("");
  const canEdit = useCanEdit();
  const refresh = useRefresh();

  useEffect(() => {
    let cancelled = false;
    getAllAccounts()
      .then((res) => {
        if (cancelled) return;
        setAccounts(res);
        if (res.length > 0) setAccountId(res[0].id);
      })
      .catch((err) => {
        notify(
          extractErrorMessage(
            err,
            `${i18nBase}.notifications.loadAccountsFailed`,
            translate,
          ),
          { type: "error" },
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!accountId) {
      setApplications([]);
      setApplicationId("");
      setGroups([]);
      setGroupId("");
      return;
    }
    let cancelled = false;
    getAccountsApplications(accountId)
      .then((res) => {
        if (!cancelled) setApplications(res);
      })
      .catch(() => {
        if (!cancelled) setApplications([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (!accountId || !applicationId) {
      setGroups([]);
      setGroupId("");
      return;
    }
    let cancelled = false;
    getAccountsGroups({ accountId, application: applicationId })
      .then((res) => {
        if (!cancelled) setGroups(res);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, applicationId]);

  const runMutation = async (id: string, fn: () => Promise<void>, okKey: string): Promise<void> => {
    setBusyId(id);
    try {
      await fn();
      notify(`${i18nBase}.notifications.${okKey}`, { type: "success" });
      refresh();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = (): void => {
    if (!deleteTarget) return;
    void runMutation(deleteTarget.id, () => deleteSchedule(accountId, deleteTarget.id), "deleted")
      .then(() => setDeleteTarget(null));
  };

  const confirmReschedule = (): void => {
    if (!rescheduleTarget || !reDatetime) return;
    // Wall-clock value (no Z): the service interprets it in the schedule's own
    // timezone; a UTC value trips its TimeZoneInfo.ConvertTimeToUtc.
    const wallClock = reDatetime.length === 16 ? reDatetime + ":00" : reDatetime;
    void runMutation(rescheduleTarget.id, () => reschedule(accountId, rescheduleTarget.id, { datetime: wallClock }), "rescheduled")
      .then(() => setRescheduleTarget(null));
  };

  const handlers: RowActionContext = {
    openDetails: (record) => setDetails(record.notification),
    onDelete: (record) => setDeleteTarget(record),
    onReschedule: (record) => {
      setReDatetime("");
      setRescheduleTarget(record);
    },
    onToggle: (record) => {
      const paused = /PAUS/i.test(record.status ?? "");
      void runMutation(
        record.id,
        () => setScheduleStatus(accountId, record.id, paused ? "ACTIVE" : "PAUSED"),
        paused ? "resumed" : "paused",
      );
    },
    canEdit,
    busyId,
  };

  const listFilter = {
    accountId,
    appId: applicationId,
    groupId: groupId,
  };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box sx={{ maxWidth }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
        </Typography>
        <FilterBar
          accountId={accountId}
          applicationId={applicationId}
          groupId={groupId}
          accounts={accounts}
          applications={applications}
          groups={groups}
          onAccountChange={setAccountId}
          onApplicationChange={setApplicationId}
          onGroupChange={setGroupId}
        />
        {accountId && (
          <List
            resource="scheduled-jobs"
            actions={false}
            filter={listFilter}
            sort={{ field: "0", order: "ASC" }}
            storeKey={false}
            disableSyncWithLocation
            perPage={10}
            exporter={false}
            sx={{
              maxWidth: "100%",
              overflow: "visible",
              "& .RaList-main, & .RaList-content": {
                overflow: "visible",
                width: "100%",
                maxWidth: "100%",
              },
            }}
          >
            <CustomDatagrid
              primarySource="id"
              bulkActionButtons={false}
              disableMobileView
            >
              <TextField
                source="id"
                label={translate(`${i18nBase}.fields.scheduleId`)}
              />
              <DateField
                source="nextExecution"
                showTime
                options={{
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                }}
              />
              <TextField source="status" />
              <FunctionField
                source="notification"
                render={() => <NotificationCell />}
              />
              <FunctionField
                source="actions"
                label={translate(`${i18nBase}.fields.actions`)}
                render={() => <ActionsCell />}
              />
            </CustomDatagrid>
          </List>
        )}
        <Dialog open={deleteTarget !== null} onClose={() => busyId === null && setDeleteTarget(null)} PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}>
          <DialogContent sx={{ textAlign: "center", pt: "24px !important" }}>
            <Typography sx={{ fontSize: "14.5px", fontWeight: 700, mb: 1 }}>
              {translate(`${i18nBase}.deleteDialog.title`, { id: deleteTarget?.id ?? "" })}
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "text.secondary" }}>
              {translate(`${i18nBase}.deleteDialog.content`)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button disabled={busyId !== null} onClick={() => setDeleteTarget(null)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button variant="contained" color="error" disabled={busyId !== null} onClick={confirmDelete}>
              {translate("ra.action.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={rescheduleTarget !== null} onClose={() => busyId === null && setRescheduleTarget(null)} PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {translate(`${i18nBase}.rescheduleDialog.title`)}
          </DialogTitle>
          <DialogContent>
            <MuiTextField
              type="datetime-local"
              fullWidth
              label={translate(`${i18nBase}.rescheduleDialog.datetime`)}
              InputLabelProps={{ shrink: true }}
              value={reDatetime}
              onChange={(e) => setReDatetime(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={busyId !== null} onClick={() => setRescheduleTarget(null)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button variant="contained" disabled={busyId !== null || !reDatetime} onClick={confirmReschedule}>
              {translate("ra.action.save")}
            </Button>
          </DialogActions>
        </Dialog>

        <NotificationDetailsDialog
          notification={details}
          onClose={() => setDetails(null)}
        />
      </Box>
    </RowActionContext.Provider>
  );
};

export default ScheduledJobsList;
